import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Alert,
  Stack,
  Card,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Send as SendIcon,
  ContentCopy as ContentCopyIcon,
  Email as EmailIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '@clerk/clerk-react';
import { 
  getInvitations, 
  createInvitation, 
  deleteInvitation, 
  resendInvitation 
} from '../../services/adminService';
import { useAppSettings } from '../../contexts/AppSettingsContext';

interface Invitation {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: 'pending' | 'accepted' | 'expired';
  role?: 'member' | 'admin';
  createdAt: string;
  expiresAt: string;
  invitationLink: string;
  clerkInvitationSent?: boolean;
}

const InvitationManagement: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteFirstName, setNewInviteFirstName] = useState('');
  const [newInviteLastName, setNewInviteLastName] = useState('');
  const [newInviteRole, setNewInviteRole] = useState<'member' | 'admin'>('member');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<Invitation | null>(null);
  const [emailError, setEmailError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const { getToken } = useAuth();
  const { settings } = useAppSettings();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const token = await getToken();
      
      if (!token) {
        setError('Authentication token not available. Please try again later.');
        setLoading(false);
        return;
      }
      
      const invitationsData = await getInvitations(token);
      setInvitations(invitationsData);
    } catch (error) {
      console.error('Error loading invitations:', error);
      setError('Failed to load invitations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvitations();
  }, [getToken]);

  const handleOpenInviteDialog = () => {
    setInviteDialogOpen(true);
    setNewInviteEmail('');
    setNewInviteFirstName('');
    setNewInviteLastName('');
    setNewInviteRole('member');
    setEmailError('');
    setFirstNameError('');
    setLastNameError('');
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
  };

  const handleOpenDeleteDialog = (invitation: Invitation) => {
    setSelectedInvitation(invitation);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedInvitation(null);
  };

  const validateForm = (): boolean => {
    let isValid = true;
    
    // Validate email
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!newInviteEmail.trim()) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!emailRegex.test(newInviteEmail)) {
      setEmailError('Invalid email format');
      isValid = false;
    } else {
      setEmailError('');
    }
    
    // Validate first name
    if (!newInviteFirstName.trim()) {
      setFirstNameError('First name is required');
      isValid = false;
    } else {
      setFirstNameError('');
    }
    
    // Validate last name
    if (!newInviteLastName.trim()) {
      setLastNameError('Last name is required');
      isValid = false;
    } else {
      setLastNameError('');
    }
    
    return isValid;
  };

  const handleSendInvitation = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      
      if (!token) {
        setError('Authentication token not available. Please try again later.');
        setLoading(false);
        return;
      }
      
      // Check if there's already a pending invitation for this email
      const existingInvitation = invitations.find(inv => 
        inv.email.toLowerCase() === newInviteEmail.toLowerCase() && 
        inv.status === 'pending' &&
        !isExpired(inv.expiresAt)
      );
      
      if (existingInvitation) {
        setError(`An active invitation for ${newInviteEmail} already exists.`);
        setLoading(false);
        setInviteDialogOpen(false);
        return;
      }
      
      const newInvitation = await createInvitation(newInviteEmail, newInviteFirstName, newInviteLastName, token, false, newInviteRole);
      setInvitations([newInvitation, ...invitations]);
      setInviteDialogOpen(false);
      
      // Check if this invitation was for an existing Clerk user
      if (newInvitation.existingClerkUser) {
        setSuccess(
          `${newInviteRole === 'admin' ? 'Admin' : 'Member'} invitation sent to ${newInviteFirstName} ${newInviteLastName} (${newInviteEmail}). ` +
          `This user already exists in Clerk and will be added to this instance when they log in.`
        );
      } else {
        setSuccess(`${newInviteRole === 'admin' ? 'Admin' : 'Member'} invitation sent to ${newInviteFirstName} ${newInviteLastName} (${newInviteEmail})`);
      }
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      
      // Enhanced error logging to debug the actual structure
      console.log('Full error object:', JSON.stringify(error, null, 2));
      
      // Get the error message - handle different error object structures
      let errorMessage = '';
      
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.response && error.response.data) {
        // Axios error format
        errorMessage = error.response.data.error || error.response.data.message || JSON.stringify(error.response.data);
        console.log('Error from response.data:', errorMessage);
      } else if (error.error) {
        // Some APIs return { error: "message" }
        errorMessage = error.error;
      } else if (error.message) {
        // Standard Error object
        errorMessage = error.message;
        
        // Special case: If the message contains our expected error text
        if (error.message.includes('A user with this email already exists')) {
          errorMessage = 'A user with this email already exists';
        }
      } else {
        // Fallback
        errorMessage = 'Failed to send invitation. Please try again.';
      }
      
      console.log('Extracted error message:', errorMessage);
      
      // Check for existing user error - use exact message from server
      if (errorMessage === 'A user with this email already exists') {
        console.log('Detected user already exists error');
        setError(`${newInviteEmail} is already a registered user in this system.`);
        setInviteDialogOpen(false);
        setLoading(false);
        return;
      }
      
      // Check if email already exists in Clerk
      if (errorMessage.includes('email already exists') || 
          errorMessage.includes('email address is taken')) {
        // Show a more informative message about existing users
        setError(
          'This email is already registered in Clerk. We will still create an invitation ' +
          'record for this instance, and the user will be granted access to this instance ' +
          'when they log in with their existing Clerk credentials.'
        );
        
        // Try to create invitation with special flag for existing users
        try {
          const token = await getToken();
          
          if (!token) {
            setError('Authentication token not available. Please try again later.');
            setLoading(false);
            return;
          }
          
          // Call a different endpoint or use a different parameter for existing users
          const newInvitation = await createInvitation(
            newInviteEmail, 
            newInviteFirstName, 
            newInviteLastName, 
            token, 
            true, // Flag to indicate this is for an existing user
            newInviteRole
          );
          
          setInvitations([newInvitation, ...invitations]);
          setInviteDialogOpen(false);
          setSuccess(
            `${newInviteRole === 'admin' ? 'Admin' : 'Member'} invitation record created for ${newInviteFirstName} ${newInviteLastName} (${newInviteEmail}). ` +
            `This user already exists in Clerk and will be added to this instance when they log in.`
          );
        } catch (secondError: any) {
          console.error('Error creating invitation for existing user:', secondError);
          const secondErrorMsg = typeof secondError === 'string' 
            ? secondError 
            : secondError.message || secondError.error || 'Failed to create invitation';
          setError(secondErrorMsg);
          setInviteDialogOpen(false);
        }
        return;
      }
      
      // General error case
      setError(errorMessage || 'Failed to send invitation. Please try again.');
      setInviteDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvitation = async () => {
    if (!selectedInvitation) return;

    try {
      setLoading(true);
      const token = await getToken();
      
      if (!token) {
        setError('Authentication token not available. Please try again later.');
        setLoading(false);
        return;
      }
      
      // The backend handles everything needed for deletion
      const response = await deleteInvitation(selectedInvitation.id, token);
      setInvitations(invitations.filter(inv => inv.id !== selectedInvitation.id));
      handleCloseDeleteDialog();
      
      // Check if there was a warning from the backend
      if (response.hasWarning) {
        setSuccess('Invitation deleted, but some related information may still exist.');
      } else {
        // Simple success message
        setSuccess('Invitation deleted successfully');
      }
    } catch (error: any) {
      console.error('Error deleting invitation:', error);
      // Show more specific error message if available
      if (error.message) {
        setError(`Failed to delete invitation: ${error.message}`);
      } else {
        setError('Failed to delete invitation. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      
      if (!token) {
        setError('Authentication token not available. Please try again later.');
        setLoading(false);
        return;
      }
      
      const updatedInvitation = await resendInvitation(invitation.id, token);
      
      setInvitations(invitations.map(inv => 
        inv.id === updatedInvitation.id ? updatedInvitation : inv
      ));
      
      setSuccess(`${(invitation.role || 'member') === 'admin' ? 'Admin' : 'Member'} invitation resent to ${invitation.firstName} ${invitation.lastName} (${invitation.email})`);
    } catch (error) {
      console.error('Error resending invitation:', error);
      setError('Failed to resend invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyInvitationLink = (link: string) => {
    // Get the current domain
    const currentDomain = window.location.origin;
    
    // Check if the link starts with "undefined" or has no domain
    let fullLink = link;
    if (link.startsWith('undefined') || !link.startsWith('http')) {
      // Extract the path part of the link (remove any domain or undefined prefix)
      const pathPart = link.replace(/^undefined|^https?:\/\/[^\/]+/i, '');
      // Create a full link with the current domain
      fullLink = `${currentDomain}${pathPart.startsWith('/') ? '' : '/'}${pathPart}`;
    }

    navigator.clipboard.writeText(fullLink)
      .then(() => {
        setSuccess('Invitation link copied to clipboard');
      })
      .catch(() => {
        setError('Failed to copy link. Please try again.');
      });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'pending': return 'primary';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const isExpired = (date: string) => {
    return new Date(date) < new Date();
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: 3 
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ mb: isMobile ? 1 : 0 }}>
          Invitation Management
        </Typography>
        <Stack 
          direction="row" 
          spacing={1}
          sx={{ 
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'flex-end' : 'flex-start'
          }}
        >
          <Button
            variant="outlined"
            onClick={loadInvitations}
            startIcon={<RefreshIcon />}
            disabled={loading}
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? "" : "Refresh"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenInviteDialog}
            size={isMobile ? "small" : "medium"}
            sx={{
              backgroundColor: settings.primaryColor,
              '&:hover': {
                backgroundColor: settings.secondaryColor,
              },
            }}
          >
            {isMobile ? "New" : "New Invitation"}
          </Button>
        </Stack>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : invitations.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No invitations found
          </Typography>
        </Paper>
      ) : isMobile ? (
        // Mobile card view
        <Grid container spacing={2}>
          {invitations.map((invitation) => (
            <Grid item xs={12} key={invitation.id}>
              <Card sx={{ mb: 1, borderRadius: 2 }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6">
                      {invitation.firstName} {invitation.lastName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={(invitation.role || 'member').toUpperCase()} 
                        size="small" 
                        color={(invitation.role || 'member') === 'admin' ? 'secondary' : 'default'}
                        variant={(invitation.role || 'member') === 'admin' ? 'filled' : 'outlined'}
                        icon={(invitation.role || 'member') === 'admin' ? <AdminIcon /> : undefined}
                      />
                      <Chip 
                        label={invitation.status.toUpperCase()} 
                        size="small" 
                        color={getStatusColor(invitation.status)}
                        icon={invitation.status === 'accepted' ? <VerifiedIcon /> : undefined}
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    {invitation.email}
                  </Typography>
                  
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" component="div">
                      Created: {formatDate(invitation.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div" 
                      sx={{ color: isExpired(invitation.expiresAt) ? 'error.main' : 'inherit' }}>
                      Expires: {formatDate(invitation.expiresAt)}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ mt: 1, mb: 1 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
                    {invitation.status === 'pending' && (
                      <>
                        <Tooltip title="Copy Invitation Link">
                          <IconButton 
                            onClick={() => copyInvitationLink(invitation.invitationLink)} 
                            size="small" 
                            color="primary"
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Resend Invitation">
                          <IconButton 
                            onClick={() => handleResendInvitation(invitation)} 
                            size="small" 
                            color="primary"
                          >
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Delete Invitation">
                      <IconButton 
                        onClick={() => handleOpenDeleteDialog(invitation)} 
                        size="small" 
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Desktop table view
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow 
                  key={invitation.id} 
                  hover
                  sx={isExpired(invitation.expiresAt) && invitation.status === 'pending' ? { backgroundColor: 'rgba(211, 47, 47, 0.05)' } : {}}
                >
                  <TableCell>
                    {invitation.firstName} {invitation.lastName}
                  </TableCell>
                  <TableCell>{invitation.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={(invitation.role || 'member').toUpperCase()} 
                      size="small" 
                      color={(invitation.role || 'member') === 'admin' ? 'secondary' : 'default'}
                      variant={(invitation.role || 'member') === 'admin' ? 'filled' : 'outlined'}
                      icon={(invitation.role || 'member') === 'admin' ? <AdminIcon /> : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={invitation.status.toUpperCase()} 
                      size="small" 
                      color={getStatusColor(invitation.status)}
                      icon={invitation.status === 'accepted' ? <VerifiedIcon /> : undefined}
                    />
                  </TableCell>
                  <TableCell>{formatDate(invitation.createdAt)}</TableCell>
                  <TableCell sx={{ color: isExpired(invitation.expiresAt) ? 'error.main' : 'inherit' }}>
                    {formatDate(invitation.expiresAt)}
                  </TableCell>
                  <TableCell align="right">
                    {invitation.status === 'pending' && (
                      <>
                        <Tooltip title="Copy Invitation Link">
                          <IconButton onClick={() => copyInvitationLink(invitation.invitationLink)}>
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Resend Invitation">
                          <IconButton onClick={() => handleResendInvitation(invitation)}>
                            <SendIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Delete Invitation">
                      <IconButton onClick={() => handleOpenDeleteDialog(invitation)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* New Invitation Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleCloseInviteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Send New Invitation</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              label="Email Address"
              variant="outlined"
              fullWidth
              value={newInviteEmail}
              onChange={(e) => setNewInviteEmail(e.target.value)}
              error={Boolean(emailError)}
              helperText={emailError}
              margin="normal"
              required
            />
            <TextField
              label="First Name"
              variant="outlined"
              fullWidth
              value={newInviteFirstName}
              onChange={(e) => setNewInviteFirstName(e.target.value)}
              error={Boolean(firstNameError)}
              helperText={firstNameError}
              margin="normal"
              required
            />
            <TextField
              label="Last Name"
              variant="outlined"
              fullWidth
              value={newInviteLastName}
              onChange={(e) => setNewInviteLastName(e.target.value)}
              error={Boolean(lastNameError)}
              helperText={lastNameError}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={newInviteRole}
                label="Role"
                onChange={(e) => setNewInviteRole(e.target.value as 'member' | 'admin')}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteDialog}>Cancel</Button>
          <Button 
            onClick={handleSendInvitation} 
            variant="contained" 
            disabled={loading}
            sx={{
              backgroundColor: settings.primaryColor,
              '&:hover': {
                backgroundColor: settings.secondaryColor,
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ pb: 2 }}>Confirm Delete</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <DialogContentText>
            Are you sure you want to delete the invitation for {selectedInvitation?.firstName} {selectedInvitation?.lastName} ({selectedInvitation?.email})?
            {selectedInvitation?.status === 'pending' && (
              <Typography 
                component="div" 
                variant="body2" 
                color="error" 
                sx={{ mt: 2, fontWeight: 'medium' }}
              >
                This action cannot be undone and the invitation link will no longer work.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button 
            onClick={handleDeleteInvitation} 
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvitationManagement; 